import { format, formatDistanceToNow } from 'date-fns';
import clsx from 'clsx';

// Date formatting utilities
export const formatDate = (date, formatStr = 'MMM dd, yyyy') => {
  if (!date) return 'N/A';
  
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }
  
  return format(dateObj, formatStr);
};

export const formatRelativeTime = (date) => {
  if (!date) return 'N/A';
  
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }
  
  return formatDistanceToNow(dateObj, { addSuffix: true });
};

// String utilities
export const truncateText = (text, maxLength = 100) => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

export const capitalizeFirst = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Color utilities
export const getLabelColor = (index) => {
  const colors = [
    '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', 
    '#10B981', '#EF4444', '#06B6D4', '#84CC16', '#F97316'
  ];
  return colors[index % colors.length];
};

export const getContrastColor = (hexColor) => {
  // Remove # if present
  const hex = hexColor.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

// Validation utilities
export const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

export const validatePassword = (password) => {
  return password.length >= 8;
};

export const validateUsername = (username) => {
  return username.length >= 3 && username.length <= 50;
};

// Class name utilities
export const cn = (...classes) => {
  return clsx(classes);
};

// Secret type utilities
export const getSecretTypeIcon = (type) => {
  const icons = {
    login: '🔐',
    password: '🔑',
    api_key: '🔑',
    note: '📝',
    card: '💳',
    ssh: '🔑',
    database: '🗄️',
    other: '📄',
  };
  return icons[type] || icons.other;
};

export const getSecretTypeLabel = (type) => {
  const labels = {
    login: 'Login',
    password: 'Password',
    api_key: 'API Key',
    note: 'Note',
    card: 'Card',
    ssh: 'SSH Key',
    database: 'Database',
    other: 'Other',
  };
  return labels[type] || labels.other;
};

// Local storage utilities
export const storage = {
  get: (key, defaultValue = null) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch {
      return defaultValue;
    }
  },
  
  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Handle storage quota exceeded
    }
  },
  
  remove: (key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Handle error
    }
  },
};

// Debounce utility
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};
